import React, { createContext, useContext, useState, useEffect } from 'react'
import { Layout, Spin, message } from 'antd'
import api from '../utils/api'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(localStorage.getItem('token'))

  const isTokenExpired = (token) => {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      return decoded.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  };
  

  // Check if user is authenticated on app start
useEffect(() => {
  const checkAuth = async () => {
    const savedToken = localStorage.getItem('token');
    const savedRefreshToken = localStorage.getItem('token');

    if (!savedToken || !savedRefreshToken) {
      setLoading(false);
      return;
    }

    try {
      
      // ตั้งค่า header
      api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

      // ตรวจสอบ token
      const response = await api.get('/api/auth/me');
      //console.log('verifying...', response);

      if (response.data.success) {
        setUser(response.data.data);
        setToken(savedToken);
      } else {
        throw new Error('Invalid token');
      }
    } catch (error) {
      //console.error('Auth check failed:', error);

      // ถ้า token หมดอายุ → ขอใหม่จาก /refresh
      if (error.response?.status === 401 && savedRefreshToken) {
        try {
          const refreshResponse = await api.post('/api/auth/refresh', { token: savedRefreshToken });
          
          if (refreshResponse.data.success) {
            const newToken = refreshResponse.data.accessToken;
            
            // เก็บ token ใหม่
            localStorage.setItem('token', newToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            
            // เรียก /me อีกครั้งเพื่อยืนยัน
            const meResponse = await api.get('/api/auth/me');
            if (meResponse.data.success) {
              setUser(meResponse.data.data);
              setToken(newToken);
              message.success('ต่ออายุเซสชันสำเร็จ');
            }
          } else {
            throw new Error('Refresh failed');
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          delete api.defaults.headers.common['Authorization'];
          message.warning('เซสชันหมดอายุ โปรดเข้าสู่ระบบใหม่');
        }
      } else {
        // ไม่มี refresh token → ออกจากระบบ
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        delete api.defaults.headers.common['Authorization'];
        message.warning('เซสชันหมดอายุ โปรดเข้าสู่ระบบใหม่');
      }
    } finally {
      setLoading(false);
    }
  };

  checkAuth();
}, []);


   // Login function
  const login = async (credentials) => {
    try {
      setLoading(true)

      const response = await api.post('/api/auth/login', credentials)

      if (response.data.success) {
        const { data } = response.data
        const { token: newToken, ...userData } = data

        // Save token and user data
        localStorage.setItem('token', newToken)
        setToken(newToken)
        setUser(userData)

        // Set token in API headers
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

        message.success('เข้าสู่ระบบสำเร็จ')

        return { success: true, user: userData }
      } else {
        message.error(response.data.message || 'เข้าสู่ระบบไม่สำเร็จ')
        return { success: false, message: response.data.message }
      }
    } catch (error) {
      console.error('Login error:', error)

      const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ'
      message.error(errorMessage)

      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Logout function
  const logout = () => {
    localStorage.removeItem('token')
    setToken(null)
    setUser(null)
    delete api.defaults.headers.common['Authorization']
    message.success('ออกจากระบบสำเร็จ')
  }

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put(`/api/users/${user.id}`, profileData)

      if (response.data.success) {
        // Refresh user data
        const userResponse = await api.get('/api/auth/me')
        if (userResponse.data.success) {
          setUser(userResponse.data.data)
        }

        message.success('ปรับปรุงข้อมูลสำเร็จ')
        return { success: true }
      } else {
        message.error(response.data.message || 'ปรับปรุงข้อมูลไม่สำเร็จ')
        return { success: false, message: response.data.message }
      }
    } catch (error) {
      console.error('Profile update error:', error)

      const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการปรับปรุงข้อมูล'
      message.error(errorMessage)

      return { success: false, message: errorMessage }
    }
  }

  // Change password
  const changePassword = async (passwordData) => {
    try {
      const response = await api.put('/api/auth/change-password', passwordData)

      if (response.data.success) {
        message.success('เปลี่ยนรหัสผ่านสำเร็จ')
        return { success: true }
      } else {
        message.error(response.data.message || 'เปลี่ยนรหัสผ่านไม่สำเร็จ')
        return { success: false, message: response.data.message }
      }
    } catch (error) {
      console.error('Password change error:', error)

      const errorMessage = error.response?.data?.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน'
      message.error(errorMessage)

      return { success: false, message: errorMessage }
    }
  }

  // Check permission
  // ✅ แก้ไข hasPermission function
  const hasPermission = (resource, action = 'read') => {

    // console.log('Resource:', resource);
    //console.log('user info:', user);

    // ป้องกัน undefined errors
    if (!user) {
      return false;
    }

    // ตรวจสอบว่า permissions มีอยู่หรือไม่ก่อน
    const permissions = user.permissions
    if (!permissions) return false  // เพิ่มจุดนี้เพื่อป้องกัน error

    // Debug logging for settings permission
    // if (resource === 'settings') {
    //   //console.log('role', user);
    //   // console.log('Checking settings permission:', {
    //   //   user: user.name,
    //   //   role: user.role,
    //   //   permissions: permissions,
    //   //   hasAll: permissions.all === true
    //   // });
    // }

    // Super admin has all permissions
    if (permissions.all === true) return true

    const resourcePermission = permissions[resource];

    if (!resourcePermission) return false;
    if (resourcePermission === true) return true;
    if (typeof resourcePermission === 'object' && resourcePermission[action] === true) return true;

    return false;
  };

  // Check role
  const hasRole = (...roles) => {
    if (!user || !user.role) return false

    console.log('hasRole', user);

    return roles.includes(user.role.code)
  }

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    hasPermission,
    hasRole,
    isAuthenticated: !!user
  }

  // ✅ ส่วนนี้แหละ ที่คุณถามว่าอยู่ไฟล์ไหน — อยู่ที่นี่เลย!
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}